import { Spacing } from '@persona-id/design-tokens';
import { CheckIcon, CircleAlertIcon } from '@persona-id/design-tokens/icons';
import Form from '@rjsf/core';
import { RJSFValidationError } from '@rjsf/utils';
import validator from '@rjsf/validator-ajv8';
import { cloneDeep } from 'lodash/fp';
import { ReactElement, useContext, useEffect, useMemo, useRef, useState } from 'react';
import styled, { ThemeContext } from 'styled-components';

import { CaseDataManager } from 'dashboard/models/case';
import { SARNarrativeTemplate } from 'dashboard/models/case-template';
import { SAR, SARData } from 'dashboard/models/sar';
import {
  Section,
  SectionedCard,
} from 'dashboard/views/DashboardSARShowView/components/SectionedCard';
import {
  fields,
  templates,
  widgets,
} from 'dashboard/views/DashboardSARShowView/FormSAR/rjsf/FormComponents';
import { sarFormConfiguration } from 'dashboard/views/DashboardSARShowView/FormSAR/SARFormSchema';
import Card from 'design-system/components/Card';
import { Heading } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip';

// see ajv validation error names and corresponding params in
// https://github.com/ajv-validator/ajv/tree/6a671057ea6aae690b5967ee26a0ddf8452c6297#error-parameters
export const transformErrors = (errors: RJSFValidationError[]) => {
  return errors.map((error) => {
    let errorMessage = error.message;
    switch (error.name) {
      case 'required':
        errorMessage = 'Please enter a value.';
        break;
      case 'minLength':
        errorMessage = `Entry must be at least ${error.params.limit} letters long.`;
        break;
      case 'maxLength':
        errorMessage = `Entry must be no longer than ${error.params.limit} letters long.`;
        break;
      case 'minItems':
        errorMessage = `Entry must have at least ${error.params.limit} items.`;
        break;
      case 'maxItems':
        errorMessage = `Entry must have no more than ${error.params.limit} items.`;
        break;
      case 'format':
        errorMessage = `Please enter a valid ${error.params.format}.`;
        break;
      case 'pattern':
        switch (error.params.pattern) {
          case '^[A-Za-z0-9]*$':
          case '^[A-Z0-9]*$':
            errorMessage =
              'Please only enter alphanumeric characters. Do not include punctuation or formatting such as hyphens, periods, and spaces within the value entry';
            break;
          case '^[A-Za-z0-9/*1*/]*$':
            errorMessage =
              'Please only enter alphanumeric characters with asterisks. Do not include punctuation or formatting such as hyphens, periods, and spaces within the value entry';
            break;
          case '^[0-9]*$':
            errorMessage = 'Please enter numeric characters only.';
            break;
          case '^[0-9]{5}([0-9]{4})?$':
            errorMessage =
              'Please enter a valid 5 or 9 digit ZIP code. Do not include punctuation or formatting such as hyphens, periods and spaces within the entry.';
            break;
          case '^([A-F0-9]{2}:){5}[A-F0-9]{2}$':
            errorMessage = 'Please enter a valid MAC address.';
            break;
          case '^(?!\\s).*(?<!\\s)$':
            errorMessage = 'Please remove leading and/or trailing spaces.';
            break;
          case '^[\x00-\x7F]*$':
            errorMessage =
              'Entry contains invalid characters. Please use only standard letters, numbers, and symbols.';
            break;
        }
        break;
    }

    if (error.message !== errorMessage) {
      return Object.assign({}, error, {
        message: errorMessage,
      });
    }
    return error;
  });
};

const ErrorListItem = styled.li`
  color: ${({ theme }) => theme.color.variants.danger.default};
  margin-left: ${Spacing[3]};
`;

export interface JSONFormSARProps {
  sar: SAR;
  caseDataManager?: CaseDataManager;
  sarNarrativeTemplates?: SARNarrativeTemplate[];
  additionalValidationErrors?: string[];
  scope?: string;
  onSubmit?: (sarData: SARData) => void;
  onAsyncValidate?: () => Promise<void>;
  // using render prop to allow the parent to handle submit and validate
  children: ({
    renderedForm,
    isDirty,
    submitForm,
    validateForm,
  }: {
    renderedForm: ReactElement;
    isDirty: boolean;
    submitForm?: () => Promise<void>;
    validateForm?: () => Promise<void>;
  }) => ReactElement;
}

export const JSONFormSAR = ({
  sar,
  caseDataManager = {},
  sarNarrativeTemplates = [],
  additionalValidationErrors = [],
  scope,
  onSubmit,
  onAsyncValidate,
  children,
}: JSONFormSARProps) => {
  /**
   * The typing here is a little rough because of RJSF, but we're just creating a map of RJSF form refs
   * so we can call validate and submit on all of the sub forms.
   */
  const formRefs = useRef<Record<string, Form<unknown, any>>>(
    Object.keys(sarFormConfiguration).reduce(
      (acc, key) => {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        acc[key] = null;
        return acc;
      },
      {} as Record<string, Form<unknown, any>>,
    ),
  );
  const [liveValidate, setLiveValidate] = useState(false);
  const [version, setVersion] = useState(0);

  const readOnlyMode = !!sar.attributes['filed-at'] || !!sar.attributes['archived-at'];
  // we are saving sarData in a ref to avoid re-rendering the form on every change
  const sarData = useRef<SARData>(cloneDeep(sar.attributes.data));
  const [formErrors, setFormErrors] = useState<Record<string, RJSFValidationError[]>>({});
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    // reset isDirty when we sync with props
    setIsDirty(false);
    sarData.current = cloneDeep(sar.attributes.data);

    // increment version to force re-render of forms after sarData.current is synced with latest props
    setVersion((prevVersion) => prevVersion + 1);
  }, [sar.attributes.data]);

  /**
   * WARNING: The rsjf Form component will overwrite its internal state with formData prop on re-render despite otherwise behaving
   * as an uncontrolled component.
   *
   * Please increment this counter by the hours you waste dealing with buggy RJSF forms.
   * If it hits 6 hours, we should bite the bullet and just us react hook forms and rewrite the form.
   * TIME WASTED DEBUGGING: 3 hours
   */
  const subForms = useMemo(() => {
    return Object.entries(sarFormConfiguration).map(([key, section]) => {
      const { schema, uiSchema } = section;

      return (
        <Form
          key={key}
          // using a callback ref here because it's a little cleaner than handling nested refs
          ref={(element) => {
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            formRefs.current[key] = element;
          }}
          schema={schema}
          uiSchema={uiSchema}
          validator={validator}
          liveValidate={liveValidate}
          onChange={({ formData }) => {
            sarData.current[key] = formData;

            // we should technically be using a deep comparison here, but it's too expensive so we assume that any change makes the form dirty
            setIsDirty(true);

            if (liveValidate) {
              validateFrontend();
            }
          }}
          onError={(errors) => {
            setFormErrors((prevFormErrors) => ({ ...prevFormErrors, [key]: errors }));
          }}
          formData={sarData.current[key]}
          templates={templates}
          fields={fields}
          formContext={{
            sarNarrativeTemplates,
            caseDataManager,
          }}
          transformErrors={transformErrors}
          widgets={widgets}
          readonly={readOnlyMode}
        >
          {/* need to pass fragment to get rid of default submit button */}
          <></>
        </Form>
      );
    });
  }, [version, liveValidate, readOnlyMode]);

  const theme = useContext(ThemeContext);

  const sections = useMemo(
    () =>
      Object.entries(sarFormConfiguration).map(([key, section], index) => {
        const { title } = section;

        let suffixIcon;

        if (formErrors[key]) {
          suffixIcon =
            formErrors[key].length > 0 ? (
              <Tooltip
                iconTrigger={<CircleAlertIcon color={theme.color.variants.caution.default} />}
              >
                {formErrors[key].length > 1
                  ? `${formErrors[key].length} validation errors remaining`
                  : `${formErrors[key].length} validation error remaining`}
              </Tooltip>
            ) : (
              <CheckIcon color={theme.color.variants.success.default} />
            );
        }

        return (
          <Section title={title} icon={suffixIcon} key={index}>
            <Card.Title>{title}</Card.Title>
            {subForms[index]}
          </Section>
        );
      }),
    [formErrors, subForms, theme],
  );

  const handleOnSubmit = async () => {
    return onSubmit?.(sarData.current);
  };

  const handleOnValidate = async () => {
    const isAllValid = validateFrontend();

    // set live validate to true after initial validation
    // HACK, allows forms to update with their validation error messages by setting state in the next tick, otherwise validation errors may not show up
    setTimeout(() => {
      setLiveValidate(true);
    });

    // check if all FE validations are passed before calling onAsyncValidate
    if (isAllValid) {
      return onAsyncValidate?.();
    }
  };

  const validateFrontend = () => {
    let isAllValid = true;

    Object.entries(formRefs.current).forEach(([key, formRef]) => {
      const isValid = formRef.validateForm();
      // have to manually clear errors if form is valid because onError doesn't get called
      if (isValid) {
        setFormErrors((prevFormErrors) => ({ ...prevFormErrors, [key]: [] }));
      } else {
        isAllValid = false;
      }
    });

    return isAllValid;
  };

  const renderedForm = (
    <>
      {additionalValidationErrors.length > 0 && (
        <Card>
          <Card.Body>
            <Heading size='sm'>Validation errors</Heading>
            <ul>
              {additionalValidationErrors.map((errorMessage) => (
                <ErrorListItem key={errorMessage}>{errorMessage}</ErrorListItem>
              ))}
            </ul>
          </Card.Body>
        </Card>
      )}
      <Card>
        <SectionedCard scope={scope}>{sections}</SectionedCard>
      </Card>
    </>
  );

  return children({
    renderedForm,
    isDirty,
    submitForm: handleOnSubmit,
    validateForm: handleOnValidate,
  });
};
